// OverviewTab.jsx
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "../../@/components/ui/card";
import { Button } from "../../@/components/ui/button";
import { FaUsers, FaCheckCircle, FaBookOpen, FaStar } from "react-icons/fa";
import {
  Eye,
  BarChart3,
  Settings,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  DollarSign,
  Users,
  Tag,
  TrendingUp,
  Link,
  ArrowRight,
} from "lucide-react";
import { courseContent } from "../data/courseContent";
import { BookOpenIcon, ExternalLink, MoveUpRight, ArrowUpRight } from "lucide-react";
import { api_endpoint } from "@/constants/constant";
import { useAuth, useUser } from "@clerk/clerk-react";
import { getClerkToken } from "@/lib/clerkToken";
import axios from "axios";
import { Spinner } from "@/components/spinner";
import { EmptyState } from "@/components/emptyState";
import { ClipLoader } from "react-spinners";
import MarkdownRenderer from "../components/markdownRenderer/MarkdownRenderer";
import ReviewCard, { ReviewList } from "../components/reviewCard";
import { apiRequest } from "@/lib/apiRequest";
import { queryKeys } from "@/lib/queryKeys";

const DEFAULT_COURSE_THUMBNAIL_URL =
  "https://lms-internal.zinduaschool.com/assets/thumbnails/default-course/default-thumbnail.png";


function formatRelativeDate(dateString) {
  const now = new Date();
  const created = new Date(dateString);
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMonths >= 3) {
    // Show actual date if older than 3 months
    return created.toLocaleDateString();
  }
  if (diffMonths >= 1) {
    return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
  }
  if (diffWeeks >= 1) {
    return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
  }
  if (diffDays >= 1) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  }
  return "Today";
}

export default function OverviewTab({
  course,
  onSwitchTab,
  onShowCompleted,
  onCourseUpdated,
  refreshKey,
  loading,
  onPreviewCourse,
}) {


  if (loading) {
    return (
      <EmptyState
        title={<Spinner />}
        description="Please wait while we load the course overview."
      />
    );
  }

  // const pathways = course?.pathways ?? []
  // const reviews = course?.reviews ?? []
  const summary = course?.summary ?? "";
  const description = course?.description ?? "";

  console.log("Course in OverviewTab:", course);
  const DIFFICULTY_REVERSE_MAP = {
    1: "Beginner",
    2: "Intermediate",
    3: "Advanced",
  };

  const { getToken, isLoaded } = useAuth();
  const { user } = useUser();

  const courseId = course?.id;

  const pathwaysQuery = useQuery({
    queryKey: queryKeys.course.pathways(courseId),
    enabled: Boolean(isLoaded && courseId),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: `/course/pathways/${courseId}`,
        auth: "required",
        signal,
      }),
  });

  const instructorsQuery = useQuery({
    queryKey: queryKeys.course.instructors(courseId),
    enabled: Boolean(isLoaded && courseId),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: `/course/${courseId}/instructors`,
        auth: "required",
        signal,
      }),
  });

  const reviewsQuery = useQuery({
    queryKey: queryKeys.course.reviews(courseId),
    enabled: Boolean(isLoaded && courseId),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: `/course/reviews/${courseId}`,
        auth: "required",
        signal,
      }),
  });

  const pathways = pathwaysQuery.data ?? [];
  const instructors = instructorsQuery.data?.instructors ?? [];
  const reviews = reviewsQuery.data?.reviews ?? [];
  const loadingPathways = pathwaysQuery.isLoading || pathwaysQuery.isFetching;
  const loadingInstructors =
    instructorsQuery.isLoading || instructorsQuery.isFetching;
  const loadingReviews = reviewsQuery.isLoading || reviewsQuery.isFetching;

  const instructorLabelFromClerk =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.primaryEmailAddress?.emailAddress ||
    String(user?.id || "").trim();

  const instructorDisplayValue = (() => {
    if (loadingInstructors) {
      return <ClipLoader size={16} color="#FE3448" />;
    }

    const fetchedNames = (instructors || [])
      .map((i) => `${i?.first_name || ""} ${i?.last_name || ""}`.trim())
      .filter(Boolean);

    const courseInstructorIds = Array.isArray(course?.instructor_ids)
      ? course.instructor_ids.map((id) => String(id))
      : [];
    const currentUserId = String(user?.id || "").trim();
    const userIsCourseInstructor =
      Boolean(currentUserId) && courseInstructorIds.includes(currentUserId);

    const fetchedHasCurrentUser = (instructors || []).some((i) => {
      const anyId = i?.id ?? i?.instructor_id ?? i?.user_id;
      return currentUserId && String(anyId) === currentUserId;
    });

    const names = [...fetchedNames];
    if (userIsCourseInstructor && !fetchedHasCurrentUser && instructorLabelFromClerk) {
      names.push(instructorLabelFromClerk);
    }

    return names.length > 0 ? names.join(", ") : "No instructors assigned.";
  })();

  // Data is now loaded via React Query; refreshKey is handled by query invalidation in the parent.

  const [page, setPage] = useState(1);
  const perPage = 4;
  const totalPages = Math.ceil(reviews.length / perPage);
  const pagedReviews = reviews.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="space-y-4 lg:p-1 bg-(--surface-base) text-(--neutral-900)">
      {/* Description & Summary */}
      <div className="flex flex-col lg:flex-row gap-4 w-full max-w-full">
        <div className="flex flex-col flex-1 min-w-0 lg:basis-0 lg:flex-2 space-y-6">
          <div className="space-y-8">
            {/* <ContentSection
              title="Summary"
              content={
                summary || (
                  <EmptyState
                    title="No Summary"
                    description="No summary available for this course."
                  />
                )
              }
            /> */}
            <ContentSection
              title="Overview"
              content={
                description || (
                  <EmptyState
                    title="No Overview"
                    description="No description available for this course."
                  />
                )
              }
              markdown
            />


            {/* Reviews */}
            <div className="space-y-6">
              <h3 className="font-heading font-semibold text-(--neutral-900)">Students Reviews</h3>
              <div className="space-y-4" id="reviewsSection">
                <ReviewList
                  reviews={reviews}
                  emptyState={
                    <EmptyState
                      icon={BookOpenIcon}
                      title="No Reviews"
                      description="No reviews for this course yet."
                    />
                  }
                  initialRowsPerPage={4}
                  rowsPerPageOptions={[4, 8, 12]}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/3 min-w-0 space-y-6 mb-3 pl-2 lg:sticky lg:top-4 lg:self-start">
          {/* Course Image/Banner */}
          <Card className="p-0 rounded-lg text-(--neutral-900) h-auto">
            <CardContent className="p-0 flex flex-col min-w-0 overflow-hidden">
              {/* Background Image */}
              <div className="p-3">
                <div className="w-full h-48 px-3 pt-3 pb-2 bg-(--neutral-100) rounded-md flex items-center justify-center overflow-hidden">
                  <img
                    src={course?.thumbnail || DEFAULT_COURSE_THUMBNAIL_URL}
                    alt={course?.title || "Course thumbnail"}
                    className="w-auto h-full max-w-full object-cover rounded-md"
                    onError={(e) => {
                      // Fallback if course thumbnail url is missing/broken
                      e.currentTarget.src = DEFAULT_COURSE_THUMBNAIL_URL;
                    }}
                  />
                </div>
              </div>

              {/* Course Details */}
              <div className="pt-4 pb-0 space-y-4  border-(--neutral-300) w-full min-w-0 overflow-hidden">
                <CourseDetail
                  icon={Link}
                  label="Slug:"
                  value={course.slug}
                  uppercase
                />
                <CourseDetail
                  icon={DollarSign}
                  label="Price:"
                  value={(() => {
                    const priceNumber = Number(course?.price ?? 0) || 0;
                    return priceNumber === 0
                      ? "Free"
                      : `KES ${priceNumber.toLocaleString()}`;
                  })()}
                />
                <CourseDetail
                  icon={Users}
                  label="Instructors:"
                  value={instructorDisplayValue}
                />
                <CourseDetail
                  icon={Tag}
                  label="Pathways:"
                  value={
                    loadingPathways ? (
                      <ClipLoader size={16} color="var(--neutral-400)" />
                    ) : pathways.length > 0
                      ? pathways.map((pathway) => pathway.title).join(", ")
                      : "No pathways assigned."
                  }
                />

                {/* <CourseDetail icon={Tag} label="Category:" value={course.category} /> */}
                <CourseDetail
                  icon={TrendingUp}
                  label="Meta:"
                  value={
                    `${DIFFICULTY_REVERSE_MAP[course.difficulty] ||
                    "Not available"
                    }, ` +
                    `${course.isOpen ? "Open" : "Closed"}, ` +
                    `${course.status === 1 ? "Published" : "Draft"} `
                  }
                />

                {/* <div className="pt-4 -mt-2">
                                        <div className="text-sm text-gray-500">
                                            Last updated <span className="font-body font-medium">{course.lastUpdated}</span>
                                            <span className="font-body font-medium">{course.lastUpdatedBy}</span>
                                        </div>
                                    </div> */}
              </div>

              <div className="flex flex-col gap-3 px-4 py-4 mb-1">
                {onPreviewCourse && (
                  <Button
                    type="button"
                    variant=""
                    className="w-full justify-center  "
                    onClick={onPreviewCourse}
                  >
                    Preview Course
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ContentSection({ title, content, markdown }) {
  return (
    <div className="space-y-4 mt-3">
      <h2 className="font-heading font-bold text-(--neutral-900)">
        {title}
      </h2>
      {markdown ? (
        <div className="prose prose-lg max-w-none text-(--neutral-800)">
          <MarkdownRenderer content={content || ""} />
        </div>
      ) : (
        <p className="font-body text-(--neutral-800) leading-relaxed">
          {content}
        </p>
      )}
    </div>
  );
}

function CourseDetail({ icon: Icon, label, value, uppercase }) {
  return (
    <div className="flex items-start w-full ml-4 text-sm text-(--neutral-900) min-w-0">
      {Icon && (
        <Icon className="mt-1 mr-2 h-4 w-4 text-(--neutral-700) shrink-0" />
      )}
      <span className="font-heading font-bold shrink-0">{label}</span>
      <span
        className={`font-body text-(--neutral-800) leading-tight ml-1 wrap-break-word flex-1 min-w-0 overflow-hidden ${uppercase ? "uppercase" : ""
          }`}
        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
      >
        {value}
      </span>
    </div>
  );
}